import { CloudOff, RefreshCw, ServerCrash } from "lucide-react";
import { Button } from "@/components/ui/button";
import { API_URL } from "@/lib/utils";

interface ServerUnavailableProps {
  onRetry: () => void;
  isRetrying?: boolean;
  retryInSeconds?: number | null;
  isOnline?: boolean;
}

export function ServerUnavailable({
  onRetry,
  isRetrying = false,
  retryInSeconds = null,
  isOnline = true,
}: ServerUnavailableProps) {
  const Icon = isOnline ? ServerCrash : CloudOff;

  return (
    <div className="flex min-h-dvh items-center justify-center bg-muted px-4 py-10">
      <div className="w-full max-w-md text-center">
        <div className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-full bg-background shadow-sm sm:h-16 sm:w-16">
          <Icon className="h-7 w-7 text-primary sm:h-8 sm:w-8" aria-hidden />
        </div>

        <h1 className="text-xl font-semibold text-foreground sm:text-2xl">
          {isOnline ? "Can't reach the server" : "You appear to be offline"}
        </h1>

        <p className="mt-3 text-sm text-muted-foreground">
          {isOnline
            ? "Prism can't reach its backend, so no data can be loaded. The service may be restarting or temporarily unavailable."
            : "Your device has no network connection. Prism will reconnect on its own once you are back online."}
        </p>

        <div className="mt-6 flex flex-col items-center gap-3">
          <Button onClick={onRetry} disabled={isRetrying} className="w-full gap-2 sm:w-auto">
            <RefreshCw className={`h-4 w-4 ${isRetrying ? "animate-spin" : ""}`} aria-hidden />
            {isRetrying ? "Checking…" : "Try again"}
          </Button>

          {/* Reassures the user that the page is not simply stuck. */}
          <p className="text-xs text-muted-foreground" aria-live="polite">
            {isRetrying
              ? "Checking the connection…"
              : retryInSeconds !== null
                ? `Checking again in ${retryInSeconds}s.`
                : "Checking again shortly."}
          </p>
        </div>

        <div className="mt-8 space-y-1 text-xs text-muted-foreground/70">
          <p>If this continues, pass this to your administrator:</p>
          {/* Its own line: a long API host breaks mid-word otherwise. */}
          <p className="break-all font-mono">No response from {API_URL}</p>
        </div>
      </div>
    </div>
  );
}

export default ServerUnavailable;
